import { useEffect, useState } from "react";
import { MapPin, Pencil, Plus, Send, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { Button, Table } from "../../../components";
import {
  GET_ADMIN_CUSTOMERS_URL,
  CREATE_ADMIN_CUSTOMER_URL,
  UPDATE_ADMIN_CUSTOMER_URL,
  GET_PRICE_LISTS_URL,
  GET_MANAGER_ROUTES_URL,
} from "../../../features/UrlHelper";
import CustomerModal from "./CustomerModal";
import PozvankaModal from "./PozvankaModal";
import "./CustomersSection.scss";

const PAYMENT_LABELS = {
  CASH: "Hotovosť",
  CARD: "Karta",
  INVOICE: "Faktúra",
  DEBT: "Dlh",
  NOT_DELIVERED: "Nedoručené",
};

function getAddressText(addressRow) {
  const addressText = String(addressRow?.address || "").trim();
  return addressText || "—";
}

function getAddressCode(addressRow) {
  return String(addressRow?.address_code || addressRow?.kod_adresy || "").trim();
}

function renderPriceListCode(priceListCode) {
  const code = String(priceListCode || "").trim();
  return (
    <span className={`customers__price-list-badge${code ? "" : " customers__price-list-badge--default"}`}>
      {code || "Štandard"}
    </span>
  );
}

function customerSearchPredicate(row, query) {
  const fullName = [row?.name, row?.surname].filter(Boolean).join(" ").toLowerCase();
  const phone = String(row?.phone_number || "").toLowerCase();
  const addresses = Array.isArray(row?.addresses) ? row.addresses : [];

  const addressMatch = addresses.some((addressRow) => {
    const code = String(addressRow?.address_code || addressRow?.kod_adresy || "").toLowerCase();
    const addressText = String(addressRow?.address || "").toLowerCase();
    return code.includes(query) || addressText.includes(query);
  });

  return fullName.includes(query) || phone.includes(query) || addressMatch;
}

function CustomersSection() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceLists, setPriceLists] = useState([]);
  // Rozvozové trasy pre priradenie k adrese (R54). Výdajná trasa sa z výberu
  // vyradí — znamenala by "na túto adresu vozíme na prevádzku".
  const [routes, setRoutes] = useState([]);
  const [pozvankaCustomer, setPozvankaCustomer] = useState(null);

  useEffect(() => {
    loadCustomers();
    loadPriceLists();
    loadRoutes();
  }, []);

  async function loadRoutes() {
    try {
      const { data } = await axios.get(GET_MANAGER_ROUTES_URL);
      setRoutes((Array.isArray(data) ? data : []).filter((r) => !r.interny_vydaj));
    } catch (error) {
      // Trasy nie sú na základnú prácu so zákazníkom nutné — bez nich sa len
      // nebude dať prideliť donáška a zvyšok formulára funguje ďalej.
      console.error("Chyba pri načítaní trás:", error);
    }
  }

  async function loadPriceLists() {
    try {
      const { data } = await axios.get(GET_PRICE_LISTS_URL);
      setPriceLists(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Chyba pri načítaní cenníkov:", error);
      alert(error?.response?.data?.message || "Cenníky sa nepodarilo načítať.");
    }
  }

  async function loadCustomers() {
    try {
      const { data } = await axios.get(GET_ADMIN_CUSTOMERS_URL);
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Chyba pri načítaní zákazníkov:", error);
      alert(error?.response?.data?.message || "Zákazníkov sa nepodarilo načítať.");
    } finally {
      setLoading(false);
    }
  }

  function otvorPozvanku(row) {
    setPozvankaCustomer(row);
  }

  function openCreateModal() {
    setEditingCustomer(null);
    setModalOpen(true);
  }

  function openEditModal(customer) {
    setEditingCustomer(customer);
    setModalOpen(true);
  }

  async function handleSubmit(payload) {
    setIsSubmitting(true);

    try {
      if (editingCustomer) {
        await axios.patch(UPDATE_ADMIN_CUSTOMER_URL(editingCustomer.id), payload);
      } else {
        await axios.post(CREATE_ADMIN_CUSTOMER_URL, payload);
      }

      setModalOpen(false);
      setEditingCustomer(null);
      await loadCustomers();
    } catch (error) {
      console.error("Chyba pri ukladaní zákazníka:", error);
      alert(error?.response?.data?.message || "Zákazníka sa nepodarilo uložiť.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const renderAddresses = (addresses) => {
    if (!Array.isArray(addresses) || addresses.length === 0) {
      return <span className="customers__no-address">—</span>;
    }

    return (
      <div className="customers__addresses">
        {addresses.map((address, index) => {
          const addressCode = getAddressCode(address);
          const paymentType = String(address?.default_payment_type || "").trim().toUpperCase();
          const paymentLabel = PAYMENT_LABELS[paymentType] || "";

          return (
            <div key={address.id || index} className="customers__address-row">
              <div className="customers__address-label">
                <MapPin className="customers__address-icon" size={13} />
                {addressCode ? <span className="customers__address-code">{addressCode}</span> : null}
              </div>
              <div className="customers__address-details">
                <span className="customers__address-text">{getAddressText(address)}</span>
                {paymentLabel ? <span className="customers__payment-badge">{paymentLabel}</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /*
   * Stav weboveho uctu. Zakaznik zalozeny obsluhou ma prazdne heslo (O5),
   * takze "ma heslo" = "uz si ucet aktivoval". Overeny e-mail je navyse --
   * bez neho si nevie obnovit zabudnute heslo.
   */
  const renderWebovyUcet = (row) => {
    if (row.ma_heslo) {
      return (
        <span className="customers__ucet customers__ucet--aktivny">
          <CheckCircle2 size={13} /> Aktívny
        </span>
      );
    }
    return (
      <span className="customers__ucet">
        {row.email ? "Pozvaný" : "Bez účtu"}
      </span>
    );
  };

  return (
    <div className="admin__section customers">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Zákazníci</h2>
        <Button variant="primary" onClick={openCreateModal}>
          <Plus size={16} /> Pridať zákazníka
        </Button>
      </div>

      <Table
        columns={[
          {
            key: "name",
            label: "Meno",
            render: (_, row) => [row.name, row.surname].filter(Boolean).join(" ") || "—",
          },
          {
            key: "addresses",
            label: "Adresy",
            render: (value) => renderAddresses(value),
          },
          {
            key: "kod_cennik",
            label: "Cenník",
            width: "120px",
            render: (value) => renderPriceListCode(value),
          },
          { key: "phone_number", label: "Telefón" },
          {
            key: "ma_heslo",
            label: "Webový účet",
            width: "150px",
            render: (_, row) => renderWebovyUcet(row),
          },
          {
            key: "actions",
            label: "Akcie",
            sortable: false,
            render: (_, row) => (
              <div className="admin__table-actions">
                <button
                  className="admin__icon-btn"
                  onClick={() => openEditModal(row)}
                  title="Upraviť"
                >
                  <Pencil size={16} />
                </button>
                {!row.ma_heslo && (
                  <button
                    className="admin__icon-btn"
                    onClick={() => otvorPozvanku(row)}
                    title="Pozvať do webového objednávania"
                  >
                    <Send size={16} />
                  </button>
                )}
              </div>
            ),
          },
        ]}
        data={customers}
        searchPredicate={customerSearchPredicate}
        searchPlaceholder="Hľadať zákazníka..."
        emptyMessage={loading ? "Načítavam..." : "Žiadni zákazníci"}
      />

      <CustomerModal
        isOpen={modalOpen}
        onClose={() => {
          if (!isSubmitting) {
            setModalOpen(false);
            setEditingCustomer(null);
          }
        }}
        customer={editingCustomer}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        priceLists={priceLists}
        routes={routes}
      />

      <PozvankaModal
        isOpen={pozvankaCustomer !== null}
        onClose={() => setPozvankaCustomer(null)}
        customer={pozvankaCustomer}
        onHotovo={loadCustomers}
      />
    </div>
  );
}

export default CustomersSection;
